/**
 * How the address holds search values: the router's `parseSearch` and
 * `stringifySearch`, shared by router.tsx and the test router
 * (test/renderAtRoute.tsx) so tests read the same addresses the app writes.
 *
 * Reading is TanStack's default: each value is JSON-parsed when it can be, so
 * `?id=42` arrives as the number 42 and the schema helpers in searchParams.ts
 * coerce it back to text.
 *
 * Writing differs from the default, which quotes any text that would parse as
 * JSON (`id: '42'` became `?id=%2242%22`). Here a scalar is written plain
 * whenever the str helper turns what is read back into the same text: `42`,
 * `true` and `null` stay readable. Text that would not survive that (`1e5`,
 * `"quoted"`, `[1]`) keeps its quotes, and lists and objects are JSON.
 */
import { parseSearchWith, stringifySearchWith } from '@tanstack/react-router';

/**
 * True when the text must be quoted to read back as itself: JSON-parsing it
 * gives a string, a list, an object, or a number written another way.
 */
function needsQuotes(text: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return false;
  }
  return typeof parsed === 'object' && parsed !== null ? true : typeof parsed === 'string' || String(parsed) !== text;
}

export const parseSearch = parseSearchWith(JSON.parse);

// stringifySearchWith quotes text when its `parser` returns, and writes it
// plain when the parser throws.
export const stringifySearch = stringifySearchWith(JSON.stringify, (text: string) => {
  if (!needsQuotes(text)) throw new Error('written plain');
  return text;
});
